import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, Grid, InputLabel, List, ListItem, MenuItem, Select,
  Snackbar, SnackbarCloseReason, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { ContasReceberService, IListagemContasReceber } from '../../../shared/services/api/Financeiro/ContasReceber';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';
import { VFieldMoney2Faturamento } from '../../../shared/forms';

export interface DialogJuntarContasProps {
  open: boolean;
  dados: IListagemContasReceber[];
  vselecionado: number;
  onClose: (value?: string) => void;
}

const MAX_PARCELAS = 24;

interface IParcelaJuntar {
  id: string;
  vencimento: string;
  valor: number;
}

const round2 = (valor: number): number => Math.round(valor * 100) / 100;

// Junta as parcelas marcadas na listagem numa única conta consolidada,
// podendo dividir o total em até 24 parcelas novas (renegociação) — mesmo
// padrão de "gerar parcelas" do Faturamento (Estoque/venda/DialogFaturamento.tsx):
// divide o total igualmente, escalona o vencimento de 30 em 30 dias, e cada
// parcela fica editável depois de gerada. Se uma das marcadas já for uma
// conta consolidada, o backend soma as demais nela em vez de criar uma nova
// (ver ConsolidacaoReceber.ts) — só aceita 1 parcela nesse caso.
export const DialogJuntarContas: React.FC<DialogJuntarContasProps> = ({
  open, dados, vselecionado, onClose,
}) => {
  const [vencimentoBase, setVencimentoBase] = useState(dayjs().format('YYYY-MM-DD'));
  const [qtdParcelas, setQtdParcelas] = useState(1);
  const [parcelas, setParcelas] = useState<IParcelaJuntar[]>([]);
  const [juntando, setJuntando] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  useEffect(() => {
    if (!open) return;
    setVencimentoBase(dayjs().format('YYYY-MM-DD'));
    setQtdParcelas(1);
  }, [open]);

  // Regera a lista de parcelas sempre que a quantidade, o primeiro
  // vencimento ou o total selecionado mudam — mesma lógica de
  // DialogFaturamento (divide igual, escalona 30 em 30 dias, última parcela
  // absorve a diferença de arredondamento). Edições manuais em uma parcela
  // (handleParcelaValorChange/handleParcelaVencimentoChange) não entram nas
  // dependências de propósito, senão essa regeração apagaria o ajuste do
  // usuário a cada digitação.
  useEffect(() => {
    if (!open) return;
    const valorParcela = round2(vselecionado / qtdParcelas);
    let acumulado = 0;
    const novasParcelas: IParcelaJuntar[] = [];
    for (let i = 0; i < qtdParcelas; i++) {
      const isLast = i === qtdParcelas - 1;
      const valor = isLast ? round2(vselecionado - acumulado) : valorParcela;
      acumulado = round2(acumulado + valor);
      novasParcelas.push({
        id: uuidv4(),
        vencimento: dayjs(vencimentoBase, 'YYYY-MM-DD').add(i * 30, 'day').format('YYYY-MM-DD'),
        valor,
      });
    }
    setParcelas(novasParcelas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vencimentoBase, qtdParcelas, vselecionado]);

  const handleParcelaValorChange = useCallback((id: string, valor: string) => {
    setParcelas((prev) => prev.map((p) => (p.id === id ? { ...p, valor: round2(Number(valor) || 0) } : p)));
  }, []);

  const handleParcelaVencimentoChange = (id: string, novoValor: string) => {
    setParcelas((prev) => prev.map((p) => (p.id === id ? { ...p, vencimento: novoValor } : p)));
  };

  const totalParcelas = round2(parcelas.reduce((acc, p) => acc + p.valor, 0));
  const diferenca = round2(totalParcelas - vselecionado);

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
    if (tms === 'success') onClose('true');
  };

  const confirmar = () => {
    if (dados.length < 2) {
      setTms('warning');
      setMsg('Selecione ao menos duas contas para juntar');
      setOpenmsg(true);
      return;
    }
    if (parcelas.some((p) => !p.vencimento || !(p.valor > 0))) {
      setTms('warning');
      setMsg('Preencha vencimento e valor de todas as parcelas');
      setOpenmsg(true);
      return;
    }
    if (Math.abs(diferenca) > 0.01) {
      setTms('warning');
      setMsg(`A soma das parcelas (${formatarMoeda(totalParcelas)}) não confere com o total selecionado (${formatarMoeda(vselecionado)})`);
      setOpenmsg(true);
      return;
    }

    setJuntando(true);
    ContasReceberService
      .Juntar(
        dados.map((conta) => conta.idrec),
        parcelas.map((p) => ({ vencimento: dayjs(p.vencimento, 'YYYY-MM-DD').toDate(), valor: p.valor })),
      )
      .then((resultado) => {
        setJuntando(false);
        if (resultado instanceof Error) {
          setTms('error');
          setMsg(resultado.message);
          setOpenmsg(true);
          return;
        }
        setTms('success');
        setMsg(`Contas juntadas na conta Nº ${resultado.numero}${parcelas.length > 1 ? ` em ${parcelas.length} parcelas` : ''} com sucesso!`);
        setOpenmsg(true);
      });
  };

  const handleCancel = () => onClose('false');

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Juntar contas</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 2500} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Contas selecionadas
        </Typography>
        <TableContainer sx={{ maxHeight: 220, mb: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dados.map((conta) => (
                <TableRow key={conta.idrec}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{conta.numero}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatarMoeda(Number(conta.vreceber))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Total</Typography>
          <Typography variant="h6" fontWeight={700}>{formatarMoeda(vselecionado)}</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Primeiro vencimento"
              InputLabelProps={{ shrink: true }}
              value={vencimentoBase}
              onChange={(e) => setVencimentoBase(e.target.value)}
              disabled={juntando}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Qtd Parcelas</InputLabel>
              <Select
                label="Qtd Parcelas"
                value={qtdParcelas}
                onChange={(e) => setQtdParcelas(Number(e.target.value))}
                disabled={juntando}
              >
                {Array.from({ length: MAX_PARCELAS }, (_, i) => i + 1).map((n) => (
                  <MenuItem key={n} value={n}>{n}x</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>Parcelas</Typography>
          <Chip label={`${parcelas.length} parcela(s)`} size="small" />
          <Chip
            label={`Total: ${formatarMoeda(totalParcelas)}`}
            size="small"
            variant="outlined"
            color={Math.abs(diferenca) > 0.01 ? 'error' : 'success'}
          />
        </Box>

        {Math.abs(diferenca) > 0.01 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Diferença de {formatarMoeda(Math.abs(diferenca))} entre a soma das parcelas e o total selecionado.
          </Alert>
        )}

        <List sx={{ pt: 0, maxHeight: 320, overflow: 'auto' }}>
          {parcelas.map((p, index) => (
            <ListItem
              key={p.id}
              disableGutters
              sx={{ pb: 1 }}
            >
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={5}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      label={`Vencimento ${index + 1}/${parcelas.length}`}
                      value={dayjs(p.vencimento)}
                      onChange={(newValue) => {
                        const inputValue = dayjs(newValue).format('YYYY-MM-DD');
                        handleParcelaVencimentoChange(p.id, inputValue);
                      }}
                      disabled={juntando}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={4}>
                  <TextField size="small" fullWidth disabled label="Parcela" value={`${index + 1}/${parcelas.length}`} />
                </Grid>
                <Grid item xs={3}>
                  <VFieldMoney2Faturamento
                    size="small"
                    fullWidth
                    id={p.id}
                    label="Valor"
                    defaultValue={String(p.valor.toFixed(2))}
                    valor={handleParcelaValorChange}
                    disabled={juntando}
                  />
                </Grid>
              </Grid>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleCancel} disabled={juntando}>Cancelar</Button>
        <Button variant="outlined" autoFocus disabled={juntando || Math.abs(diferenca) > 0.01} onClick={confirmar}>
          {juntando ? 'Juntando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
